import React, { useState } from "react";
import { CallSplit as ChannelIcon } from "@mui/icons-material";
import { Order } from "../../../types";
import { orderService } from "../../../services/orderService";
import { orderChannelService } from "../../../services/orderChannelService";
import { ConfirmChangeDialog } from "./ConfirmChangeDialog";
import { CourierPicker } from "./CourierPicker";
import { PickOption, QuickPickChip } from "./QuickPickChip";
import { Notify, useQuickEditSave } from "./useQuickEditSave";

interface OrderDeliveryPickersProps {
    order: Order;
    lockedReason: string | null;
    onOrderChanged: (order: Order) => void;
    notify: Notify;
}

const loadChannels = async (): Promise<PickOption[]> =>
    (await orderChannelService.getOrderChannelsSimple(true)).map((c) => ({
        id: c.order_channel_id,
        name: c.name,
        detail: c.type === "ONLINE" ? "Online" : "Offline",
    }));

/** The drawer's courier and channel chips — click to change, confirm to save. */
export const OrderDeliveryPickers: React.FC<OrderDeliveryPickersProps> = ({
    order,
    lockedReason,
    onOrderChanged,
    notify,
}) => {
    // `pending` outlives `open` so the dialog keeps its text while it animates
    // closed; `from` is fixed at pick time (the order refreshes first).
    const [pending, setPending] = useState<{ option: PickOption; from: string } | null>(null);
    const [open, setOpen] = useState(false);
    const { saving, error, setError, save } = useQuickEditSave(order.order_id, onOrderChanged, notify);

    const courier = order.delivery_service
        ? { id: order.delivery_service.delivery_service_id, name: order.delivery_service.name }
        : null;
    const channel = order.order_channel
        ? { id: order.order_channel.order_channel_id, name: order.order_channel.name }
        : null;

    const confirm = async () => {
        if (!pending) return;
        const { option } = pending;
        const saved = await save(
            () => orderService.updateOrder(order.order_id, { order_channel_id: option.id }),
            `Order channel changed to ${option.name}`,
        );
        if (saved) setOpen(false);
    };

    return (
        <>
            <CourierPicker
                orderId={order.order_id}
                courier={courier}
                consignmentId={order.consignment_id}
                lockedReason={lockedReason}
                onOrderChanged={onOrderChanged}
                notify={notify}
            />
            <QuickPickChip
                icon={<ChannelIcon fontSize="small" />}
                value={channel}
                placeholder="Set channel"
                menuTitle="Order channel"
                color="secondary"
                loadOptions={loadChannels}
                onPick={(option) => {
                    setError("");
                    setPending({ option, from: channel?.name ?? "None" });
                    setOpen(true);
                }}
                lockedReason={lockedReason}
            />
            <ConfirmChangeDialog
                open={open}
                title="Change order channel?"
                rows={pending ? [{ label: "Order channel", from: pending.from, to: pending.option.name }] : []}
                error={error}
                saving={saving}
                onConfirm={confirm}
                onCancel={() => setOpen(false)}
            />
        </>
    );
};
